import { useCallback, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";

import CheckboxFormField from "components/checkbox-form-field";

import type { SelectSaleConfigurationValues } from "../../word-processor-select-sale-configuration-dialog.component.tsx";
import SettingsAccordionSummary from "../settings-accordion-summary.component";

// DeviceSettingsGroup component with setValue prop support
interface DeviceSettingsGroupProps {
  readonly label: string;
  readonly namePrefix: string;
  readonly disableColor?: boolean;
  readonly disableBattery?: boolean;
  readonly disableEar?: boolean;
  readonly expanded?: boolean;
  readonly onChange?: (event: React.SyntheticEvent, expanded: boolean) => void;
}

export default function DeviceSettingsGroup(props: DeviceSettingsGroupProps) {
  const {
    label,
    namePrefix,
    disableColor,
    disableBattery,
    disableEar,
    expanded,
    onChange
  } = props;

  const { control, setValue } = useFormContext<SelectSaleConfigurationValues>();

  // Get all field names for this device group
  const fieldNames = useMemo(() => {
    const fields = [
      `${namePrefix}_show_product`,
      `${namePrefix}_show_manufacturer`,
      `${namePrefix}_show_model`,
      `${namePrefix}_show_serial`,
      `${namePrefix}_show_mfr_warranty_exp`,
      `${namePrefix}_show_lnd_warranty_exp`,
      `${namePrefix}_show_lnd_deductible`,
      `${namePrefix}_show_price`,
      `${namePrefix}_show_quantity`,
      `${namePrefix}_show_patient_resp`,
      `${namePrefix}_show_insurance_resp`,
      `${namePrefix}_show_managed_care_resp`,
      `${namePrefix}_show_cpt_code`
    ];
    if (!disableColor) {
      fields.push(`${namePrefix}_show_color`);
    }
    if (!disableBattery) {
      fields.push(`${namePrefix}_show_battery`);
    }
    if (!disableEar) {
      fields.push(`${namePrefix}_show_ear`);
    }
    // Add device-specific fields
    if (namePrefix === "earmolds") {
      fields.push(
        `${namePrefix}_show_material`,
        `${namePrefix}_show_canal_length`,
        `${namePrefix}_show_canal_venting`
      );
    } else if (namePrefix === "receivers") {
      fields.push(`${namePrefix}_show_gain`, `${namePrefix}_show_length`);
    } else if (namePrefix === "domes") {
      fields.push(
        `${namePrefix}_show_dome_type`,
        `${namePrefix}_show_dome_size`
      );
    } else if (namePrefix === "hearing_aids") {
      fields.push(`${namePrefix}_show_service_plan_exp`);
    }
    return fields;
  }, [namePrefix, disableColor, disableBattery, disableEar]);

  // Watch all fields and count selected ones
  const watchedValues = useWatch({
    control,
    name: fieldNames as any
  });

  const selectedCount = useMemo(() => {
    return Array.isArray(watchedValues)
      ? watchedValues.filter(Boolean).length
      : 0;
  }, [watchedValues]);

  const handleSelectAll = useCallback(
    (event: any) => {
      // set all the checkbox values to true
      event.preventDefault();
      event.stopPropagation();
      (setValue as any)(`${namePrefix}_show_product`, true);
      (setValue as any)(`${namePrefix}_show_manufacturer`, true);
      (setValue as any)(`${namePrefix}_show_model`, true);
      if (!disableColor) {
        (setValue as any)(`${namePrefix}_show_color`, true);
      }
      if (!disableBattery) {
        (setValue as any)(`${namePrefix}_show_battery`, true);
      }
      if (!disableEar) {
        (setValue as any)(`${namePrefix}_show_ear`, true);
      }
      (setValue as any)(`${namePrefix}_show_serial`, true);
      (setValue as any)(`${namePrefix}_show_mfr_warranty_exp`, true);
      (setValue as any)(`${namePrefix}_show_lnd_warranty_exp`, true);
      (setValue as any)(`${namePrefix}_show_lnd_deductible`, true);
      (setValue as any)(`${namePrefix}_show_price`, true);
      (setValue as any)(`${namePrefix}_show_quantity`, true);
      (setValue as any)(`${namePrefix}_show_patient_resp`, true);
      (setValue as any)(`${namePrefix}_show_insurance_resp`, true);
      (setValue as any)(`${namePrefix}_show_managed_care_resp`, true);
      (setValue as any)(`${namePrefix}_show_cpt_code`, true);

      // Add device-specific fields
      if (namePrefix === "earmolds") {
        (setValue as any)(`${namePrefix}_show_material`, true);
        (setValue as any)(`${namePrefix}_show_canal_length`, true);
        (setValue as any)(`${namePrefix}_show_canal_venting`, true);
      } else if (namePrefix === "receivers") {
        (setValue as any)(`${namePrefix}_show_gain`, true);
        (setValue as any)(`${namePrefix}_show_length`, true);
      } else if (namePrefix === "domes") {
        (setValue as any)(`${namePrefix}_show_dome_type`, true);
        (setValue as any)(`${namePrefix}_show_dome_size`, true);
      } else if (namePrefix === "hearing_aids") {
        (setValue as any)(`${namePrefix}_show_service_plan_exp`, true);
      }
    },
    [setValue, namePrefix, disableColor, disableBattery, disableEar]
  );

  const handleDeselectAll = useCallback(
    (event: any) => {
      // set all the checkbox values to false
      event.preventDefault();
      event.stopPropagation();
      (setValue as any)(`${namePrefix}_show_product`, false);
      (setValue as any)(`${namePrefix}_show_manufacturer`, false);
      (setValue as any)(`${namePrefix}_show_model`, false);
      if (!disableColor) {
        (setValue as any)(`${namePrefix}_show_color`, false);
      }
      if (!disableBattery) {
        (setValue as any)(`${namePrefix}_show_battery`, false);
      }
      if (!disableEar) {
        (setValue as any)(`${namePrefix}_show_ear`, false);
      }
      (setValue as any)(`${namePrefix}_show_serial`, false);
      (setValue as any)(`${namePrefix}_show_mfr_warranty_exp`, false);
      (setValue as any)(`${namePrefix}_show_lnd_warranty_exp`, false);
      (setValue as any)(`${namePrefix}_show_lnd_deductible`, false);
      (setValue as any)(`${namePrefix}_show_price`, false);
      (setValue as any)(`${namePrefix}_show_quantity`, false);
      (setValue as any)(`${namePrefix}_show_patient_resp`, false);
      (setValue as any)(`${namePrefix}_show_insurance_resp`, false);
      (setValue as any)(`${namePrefix}_show_managed_care_resp`, false);
      (setValue as any)(`${namePrefix}_show_cpt_code`, false);

      // Add device-specific fields
      if (namePrefix === "earmolds") {
        (setValue as any)(`${namePrefix}_show_material`, false);
        (setValue as any)(`${namePrefix}_show_canal_length`, false);
        (setValue as any)(`${namePrefix}_show_canal_venting`, false);
      } else if (namePrefix === "receivers") {
        (setValue as any)(`${namePrefix}_show_gain`, false);
        (setValue as any)(`${namePrefix}_show_length`, false);
      } else if (namePrefix === "domes") {
        (setValue as any)(`${namePrefix}_show_dome_type`, false);
        (setValue as any)(`${namePrefix}_show_dome_size`, false);
      } else if (namePrefix === "hearing_aids") {
        (setValue as any)(`${namePrefix}_show_service_plan_exp`, false);
      }
    },
    [setValue, namePrefix, disableColor, disableBattery, disableEar]
  );

  return (
    <Accordion expanded={expanded} onChange={onChange}>
      <SettingsAccordionSummary
        title={label}
        expanded={expanded}
        onSelectAll={handleSelectAll}
        onDeselectAll={handleDeselectAll}
        selectedCount={selectedCount}
      />
      <AccordionDetails>
        <Stack ml={2} mb={2} mt={-2}>
          {!disableEar && (
            <FormControlLabel
              label="Show Ear"
              sx={{ mb: -1 }}
              control={
                <CheckboxFormField
                  name={`${namePrefix}_show_ear` as any}
                  control={control}
                  sx={{ ml: 0 }}
                />
              }
            />
          )}
          <FormControlLabel
            label="Show CPT Code"
            sx={{ mb: -1 }}
            control={
              <CheckboxFormField
                name={`${namePrefix}_show_cpt_code` as any}
                control={control}
                sx={{ ml: 0 }}
              />
            }
          />
          <FormControlLabel
            label="Show Product"
            sx={{ mb: -1 }}
            control={
              <CheckboxFormField
                name={`${namePrefix}_show_product` as any}
                control={control}
              />
            }
          />

          <FormControlLabel
            label="Show Manufacturer"
            sx={{ mb: -1 }}
            control={
              <CheckboxFormField
                name={`${namePrefix}_show_manufacturer` as any}
                control={control}
                sx={{ ml: 0 }}
              />
            }
          />

          <FormControlLabel
            label="Show Model"
            sx={{ mb: -1 }}
            control={
              <CheckboxFormField
                name={`${namePrefix}_show_model` as any}
                control={control}
                sx={{ ml: 0 }}
              />
            }
          />

          {!disableColor && (
            <FormControlLabel
              label="Show Color"
              sx={{ mb: -1 }}
              control={
                <CheckboxFormField
                  name={`${namePrefix}_show_color` as any}
                  control={control}
                  sx={{ ml: 0 }}
                />
              }
            />
          )}

          {!disableBattery && (
            <FormControlLabel
              label="Show Battery"
              sx={{ mb: -1 }}
              control={
                <CheckboxFormField
                  name={`${namePrefix}_show_battery` as any}
                  control={control}
                  sx={{ ml: 0 }}
                />
              }
            />
          )}

          <FormControlLabel
            label="Show Serial #"
            sx={{ mb: -1 }}
            control={
              <CheckboxFormField
                name={`${namePrefix}_show_serial` as any}
                control={control}
                sx={{ ml: 0 }}
              />
            }
          />

          <FormControlLabel
            label="Show MFR Warranty Expiration Date"
            sx={{ mb: -1 }}
            control={
              <CheckboxFormField
                name={`${namePrefix}_show_mfr_warranty_exp` as any}
                control={control}
                sx={{ ml: 0 }}
              />
            }
          />

          <FormControlLabel
            label="Show L&D Warranty Expiration Date"
            sx={{ mb: -1 }}
            control={
              <CheckboxFormField
                name={`${namePrefix}_show_lnd_warranty_exp` as any}
                control={control}
                sx={{ ml: 0 }}
              />
            }
          />

          {namePrefix === "hearing_aids" && (
            <FormControlLabel
              label="Show Service Plan Expiration Date"
              sx={{ mb: -1 }}
              control={
                <CheckboxFormField
                  name={`${namePrefix}_show_service_plan_exp` as any}
                  control={control}
                  sx={{ ml: 0 }}
                />
              }
            />
          )}

          <FormControlLabel
            label="Show L&D Deductible"
            sx={{ mb: -1 }}
            control={
              <CheckboxFormField
                name={`${namePrefix}_show_lnd_deductible` as any}
                control={control}
                sx={{ ml: 0 }}
              />
            }
          />

          <FormControlLabel
            label="Show Price"
            sx={{ mb: -1 }}
            control={
              <CheckboxFormField
                name={`${namePrefix}_show_price` as any}
                control={control}
                sx={{ ml: 0 }}
              />
            }
          />

          <FormControlLabel
            label="Show Quantity"
            sx={{ mb: -1 }}
            control={
              <CheckboxFormField
                name={`${namePrefix}_show_quantity` as any}
                control={control}
                sx={{ ml: 0 }}
              />
            }
          />

          <FormControlLabel
            label="Show Patient Responsibility"
            sx={{ mb: -1 }}
            control={
              <CheckboxFormField
                name={`${namePrefix}_show_patient_resp` as any}
                control={control}
                sx={{ ml: 0 }}
              />
            }
          />

          <FormControlLabel
            label="Show Insurance Responsibility"
            sx={{ mb: -1 }}
            control={
              <CheckboxFormField
                name={`${namePrefix}_show_insurance_resp` as any}
                control={control}
                sx={{ ml: 0 }}
              />
            }
          />

          <FormControlLabel
            label="Show Managed Care Responsibility"
            sx={{ mb: -1 }}
            control={
              <CheckboxFormField
                name={`${namePrefix}_show_managed_care_resp` as any}
                control={control}
                sx={{ ml: 0 }}
              />
            }
          />

          {/* Device-specific fields */}
          {namePrefix === "earmolds" && (
            <>
              <FormControlLabel
                label="Show Material"
                sx={{ mb: -1 }}
                control={
                  <CheckboxFormField
                    name={`${namePrefix}_show_material` as any}
                    control={control}
                    sx={{ ml: 0 }}
                  />
                }
              />
              <FormControlLabel
                label="Show Canal Length"
                sx={{ mb: -1 }}
                control={
                  <CheckboxFormField
                    name={`${namePrefix}_show_canal_length` as any}
                    control={control}
                    sx={{ ml: 0 }}
                  />
                }
              />
              <FormControlLabel
                label="Show Canal Venting"
                sx={{ mb: -1 }}
                control={
                  <CheckboxFormField
                    name={`${namePrefix}_show_canal_venting` as any}
                    control={control}
                    sx={{ ml: 0 }}
                  />
                }
              />
            </>
          )}

          {namePrefix === "receivers" && (
            <>
              <FormControlLabel
                label="Show Gain"
                sx={{ mb: -1 }}
                control={
                  <CheckboxFormField
                    name={`${namePrefix}_show_gain` as any}
                    control={control}
                    sx={{ ml: 0 }}
                  />
                }
              />
              <FormControlLabel
                label="Show Length"
                sx={{ mb: -1 }}
                control={
                  <CheckboxFormField
                    name={`${namePrefix}_show_length` as any}
                    control={control}
                    sx={{ ml: 0 }}
                  />
                }
              />
            </>
          )}

          {namePrefix === "domes" && (
            <>
              <FormControlLabel
                label="Show Dome Type"
                sx={{ mb: -1 }}
                control={
                  <CheckboxFormField
                    name={`${namePrefix}_show_dome_type` as any}
                    control={control}
                    sx={{ ml: 0 }}
                  />
                }
              />
              <FormControlLabel
                label="Show Dome Size"
                sx={{ mb: -1 }}
                control={
                  <CheckboxFormField
                    name={`${namePrefix}_show_dome_size` as any}
                    control={control}
                    sx={{ ml: 0 }}
                  />
                }
              />
            </>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
